import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  FloatingLabel,
  Spinner,
  Container,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useAuth } from "../../contexts/authContext";
import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";

// Define validation schema using yup
const schema = yup.object().shape({
  currentPassword: yup
    .string()
    .required("Current password is required")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+{}|:"<>?~`\-=[\]\\;',./])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  newPassword: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+{}|:"<>?~`\-=[\]\\;',./])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm new password is required")
    .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
});

const ChangePassword = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema), // Integrate yup with react-hook-form
  });

  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const response = await apiService.put("/user-profile/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      showNotification("success", response.message, 5000);
      reset();
      setTimeout(async () => {
        await logout();
        navigate("/login");
      }, 1000);
    } catch (error) {
      showNotification("danger", error || "Failed to change password.", 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="p-0">
      <Row>
        <Col xs={12} md={10} lg={8} xl={7} xxl={6}>
          <Card className="p-4 border-0">
            <Card.Body>
              <h1 className="mb-2 fs-5">Change Password</h1>
              <p className="mb-0 text-light-emphasis fw-light">
                Please enter your current password and the new password below.
              </p>
              <hr className="mt-3 mb-4" />
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FloatingLabel
                  controlId="txtCurrentPassword"
                  label="Current Password"
                  className="mb-4"
                >
                  <Form.Control
                    type="password"
                    placeholder="Current Password"
                    {...register("currentPassword")}
                    isInvalid={!!errors.currentPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.currentPassword?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <FloatingLabel
                  controlId="txtNewPassword"
                  label="New Password"
                  className="mb-4"
                >
                  <Form.Control
                    type="password"
                    placeholder="New Password"
                    {...register("newPassword")}
                    isInvalid={!!errors.newPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.newPassword?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <FloatingLabel
                  controlId="txtConfirmPassword"
                  label="Confirm New Password"
                  className="mb-4"
                >
                  <Form.Control
                    type="password"
                    placeholder="Confirm New Password"
                    {...register("confirmPassword")}
                    isInvalid={!!errors.confirmPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    disabled={isLoading}
                    type="submit"
                    className="btn-main"
                  >
                    {isLoading ? (
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ChangePassword;
